import React, { useEffect, useState } from "react";
import InfoPage from "./InfoPage";
import { getCanarySnapshot, refreshCanary } from "../../lib/canary";
import { getLastIapSyncResult } from "../../lib/iapAutoSync";
import { getCachedEntitlement, isCachedEntitlementActive } from "../../lib/entitlementCache";
import { isNative } from "../../lib/platform";

/**
 * T49 — Settings → About → Connection Diagnostics.
 *
 * Surfaces on-device proof of which backend the running .ipa was
 * built against AND which routes that backend actually serves. The
 * existence of this screen is the architectural fix for Build 70's
 * "is it the gateway or the bundle?" mystery — we no longer guess.
 *
 * T49.E (Build 73) — App build is read from Capacitor's native
 * `App.getInfo()` so the screen ALWAYS displays the real
 * MARKETING_VERSION and CURRENT_PROJECT_VERSION (e.g. "1.0.73") set
 * by Xcode at archive time, not the JS bundle's "dev" string. On
 * web/preview where Capacitor isn't native, falls back to
 * REACT_APP_BUILD_TAG, then "dev".
 */

const ENV_BUILD_TAG = process.env.REACT_APP_BUILD_TAG || "";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "(env missing)";

const ROUTE_LABELS = {
  authApple: "Apple sign-in (/api/auth/apple)",
  iapApply: "Pro entitlement sync (/api/iap/apple/apply)",
  iapSync: "RevenueCat sync (/api/iap/sync)",
  health: "Health probe (/api/health)",
  authMe: "Session lookup (/api/auth/me)",
};

function Row({ label, value, tone = "neutral", testId }) {
  const palette = {
    ok: "text-emerald-700 bg-emerald-50 border-emerald-200",
    bad: "text-red-700 bg-red-50 border-red-200",
    warn: "text-amber-700 bg-amber-50 border-amber-200",
    neutral: "text-navy/80 bg-white border-navy/10",
  }[tone];
  return (
    <div
      className={`flex items-start justify-between gap-3 px-4 py-3 rounded-2xl border ${palette}`}
      data-testid={testId}
    >
      <div className="font-jakarta text-sm font-semibold">{label}</div>
      <div
        className="font-mono text-xs text-right break-all max-w-[60%]"
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value}
      </div>
    </div>
  );
}

export default function ConnectionDiagnostics({ onBack }) {
  const [snap, setSnap] = useState(() => getCanarySnapshot());
  const [busy, setBusy] = useState(false);
  const [lastSync, setLastSync] = useState(() => getLastIapSyncResult());
  const [entCache, setEntCache] = useState(() => getCachedEntitlement());
  const [entActive, setEntActive] = useState(() => isCachedEntitlementActive());
  const [appBuild, setAppBuild] = useState(() => ENV_BUILD_TAG || "dev");

  useEffect(() => {
    // T49.E — Ask Capacitor for the REAL native build info so we
    // never display "dev" on a TestFlight archive. On non-native
    // (web preview) the import is a no-op and we keep the env tag.
    (async () => {
      if (!isNative()) return;
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        // `version` = CFBundleShortVersionString (MARKETING_VERSION, e.g. "1.0.0")
        // `build`   = CFBundleVersion (CURRENT_PROJECT_VERSION, e.g. "73")
        const v = info?.version || "";
        const b = info?.build || "";
        if (v || b) {
          setAppBuild(`${v}${v && b ? " (" : ""}${b}${v && b ? ")" : ""}`);
        }
      } catch { /* native bridge unavailable — leave env fallback */ }
    })();
  }, []);

  useEffect(() => {
    // Auto-refresh on mount so the user always sees current state.
    void doRecheck();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function doRecheck() {
    setBusy(true);
    try {
      const next = await refreshCanary();
      setSnap(next);
      setLastSync(getLastIapSyncResult());
      setEntCache(getCachedEntitlement());
      setEntActive(isCachedEntitlementActive());
    } finally {
      setBusy(false);
    }
  }

  const overallOk = !!snap?.ok;
  const overallTone = !snap ? "neutral" : overallOk ? "ok" : "bad";
  const overallLabel = !snap
    ? "Probing…"
    : overallOk
      ? "All routes live"
      : "Backend or route unavailable";

  return (
    <InfoPage title="Connection Diagnostics" onBack={onBack}>
      <div className="space-y-3" data-testid="connection-diagnostics">
        <Row
          label="Status"
          value={overallLabel}
          tone={overallTone}
          testId="diag-overall-status"
        />
        <Row
          label="App build"
          value={appBuild}
          testId="diag-app-build"
        />
        <Row
          label="Bundled backend URL"
          value={BACKEND_URL}
          testId="diag-backend-url"
        />
        {snap?.backendBuild && (
          <Row
            label="Backend build"
            value={snap.backendBuild}
            testId="diag-backend-build"
          />
        )}
        {snap?.checkedAt && (
          <Row
            label="Last checked"
            value={new Date(snap.checkedAt).toLocaleString()}
            testId="diag-checked-at"
          />
        )}
      </div>

      <h3 className="font-jakarta font-extrabold text-navy text-base mt-6 mb-2">
        Routes
      </h3>
      <div className="space-y-3" data-testid="diag-routes">
        {Object.keys(ROUTE_LABELS).map((key) => {
          const live = !!snap?.routes?.[key];
          return (
            <Row
              key={key}
              label={ROUTE_LABELS[key]}
              value={live ? "Live" : "Unavailable"}
              tone={!snap ? "neutral" : live ? "ok" : "bad"}
              testId={`diag-route-${key}`}
            />
          );
        })}
      </div>

      <h3 className="font-jakarta font-extrabold text-navy text-base mt-6 mb-2">
        Subscription
      </h3>
      <div className="space-y-3" data-testid="diag-subscription">
        <Row
          label="Local entitlement (Apple)"
          value={
            entCache
              ? entActive
                ? `Active · ${entCache.productId || "unknown SKU"}`
                : "Cached but expired"
              : "None on device"
          }
          tone={!entCache ? "neutral" : entActive ? "ok" : "warn"}
          testId="diag-entitlement-local"
        />
        {entCache?.expirationDate && (
          <Row
            label="Apple expiration"
            value={new Date(entCache.expirationDate).toLocaleString()}
            testId="diag-entitlement-expires"
          />
        )}
        <Row
          label="Last sync result"
          value={
            !lastSync
              ? "Never run"
              : lastSync.ok
                ? `OK · ${lastSync.source} · ${new Date(lastSync.at).toLocaleString()}`
                : `Failed · ${lastSync.error?.status || ""} ${lastSync.error?.message || ""}`.trim()
          }
          tone={!lastSync ? "neutral" : lastSync.ok ? "ok" : "warn"}
          testId="diag-last-sync"
        />
      </div>

      {snap?.error && (
        <div
          className="mt-4 px-4 py-3 rounded-2xl border border-red-200 bg-red-50 text-red-700 font-mono text-xs"
          data-testid="diag-error"
        >
          <div className="font-semibold mb-1 font-jakarta">
            Canary request failed
          </div>
          HTTP {snap.error.status ?? "—"} · {snap.error.message}
        </div>
      )}

      <button
        type="button"
        onClick={doRecheck}
        disabled={busy}
        data-testid="diag-recheck-btn"
        className="mt-6 w-full bg-navy text-white font-jakarta font-bold rounded-full px-4 py-3 disabled:opacity-60"
      >
        {busy ? "Re-checking…" : "Re-check now"}
      </button>

      <p className="mt-3 text-xs text-navy/60 font-jakarta">
        This panel verifies your iPhone can reach every backend route the app
        depends on. If any row says <em>Unavailable</em>, sign-in or
        subscription features may fail until the backend is reachable again.
      </p>
    </InfoPage>
  );
}
